import type { Transcript, TranscriptSegment, Speaker } from '@/types';

// Default speaker colors (vibrant, accessible)
const SPEAKER_COLORS = [
  '#3b82f6', // Blue
  '#10b981', // Emerald
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#84cc16', // Lime
  '#f97316', // Orange
  '#6366f1', // Indigo
];

/**
 * Parse timestamp string to milliseconds
 * Supports formats: "00:00.000", "00:00:00.000", "00:00", "00:00:00"
 */
export function parseTimestamp(timestamp: string): number {
  const cleaned = timestamp.trim();
  
  // Handle HH:MM:SS.mmm or HH:MM:SS
  const fullMatch = cleaned.match(/^(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/);
  if (fullMatch) {
    const [, hours, minutes, seconds, ms = '0'] = fullMatch;
    return (
      parseInt(hours) * 3600000 +
      parseInt(minutes) * 60000 +
      parseInt(seconds) * 1000 +
      parseInt(ms.padEnd(3, '0'))
    );
  }
  
  // Handle MM:SS.mmm or MM:SS
  const shortMatch = cleaned.match(/^(\d{1,2}):(\d{2})(?:\.(\d{1,3}))?$/);
  if (shortMatch) {
    const [, minutes, seconds, ms = '0'] = shortMatch;
    return (
      parseInt(minutes) * 60000 +
      parseInt(seconds) * 1000 +
      parseInt(ms.padEnd(3, '0'))
    );
  }
  
  // Handle pure seconds with optional milliseconds
  const secondsMatch = cleaned.match(/^(\d+)(?:\.(\d{1,3}))?$/);
  if (secondsMatch) {
    const [, seconds, ms = '0'] = secondsMatch;
    return parseInt(seconds) * 1000 + parseInt(ms.padEnd(3, '0'));
  }
  
  console.warn(`Could not parse timestamp: ${timestamp}`);
  return 0;
}

/**
 * Format milliseconds to display string (always in hh:mm:ss format)
 */
export function formatTimestamp(ms: number, includeMs = false): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const milliseconds = ms % 1000;
  
  const base = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  return includeMs ? `${base}.${milliseconds.toString().padStart(3, '0')}` : base;
}

/**
 * Format milliseconds as absolute time based on recording date (24-hour format)
 */
export function formatAbsoluteTime(ms: number, recordingDate: Date | string): string {
  const baseDate = typeof recordingDate === 'string' ? new Date(recordingDate) : recordingDate;
  const absoluteTime = new Date(baseDate.getTime() + ms);
  const hours = absoluteTime.getHours();
  const minutes = absoluteTime.getMinutes();
  const seconds = absoluteTime.getSeconds();
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Format milliseconds as absolute time (24-hour format)
 */
export function formatAbsoluteTime24h(ms: number, recordingDate: Date | string): string {
  const baseDate = typeof recordingDate === 'string' ? new Date(recordingDate) : recordingDate;
  const absoluteTime = new Date(baseDate.getTime() + ms);
  const hours = absoluteTime.getHours();
  const minutes = absoluteTime.getMinutes();
  const seconds = absoluteTime.getSeconds();
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Format duration in human-readable form
 */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

/**
 * Parse transcript text in the format:
 * [00:00.000 → 00:10.960] **SPEAKER_02:** Text here
 * [00:10.960 → 00:20.960] **Speaker ?:** More text
 */
export function parseTranscriptText(
  text: string,
  options: { title?: string; recordingDate?: Date } = {}
): Transcript {
  const lines = text.trim().split('\n').filter(line => line.trim());
  const segments: TranscriptSegment[] = [];
  const speakerMap = new Map<string, Speaker>();
  let colorIndex = 0;
  
  // Regex patterns for different formats
  const patterns = [
    // [00:00.000 → 00:10.960] **SPEAKER_02:** Text
    /^\[(\d{1,2}:\d{2}(?::\d{2})?(?:\.\d{1,3})?)\s*[→→-]\s*(\d{1,2}:\d{2}(?::\d{2})?(?:\.\d{1,3})?)\]\s*\*\*([^:*]+)\*\*:\s*(.+)$/,
    // [00:00.000 - 00:10.960] SPEAKER_02: Text
    /^\[(\d{1,2}:\d{2}(?::\d{2})?(?:\.\d{1,3})?)\s*[→→-]\s*(\d{1,2}:\d{2}(?::\d{2})?(?:\.\d{1,3})?)\]\s*([^:]+):\s*(.+)$/,
    // 00:00.000 - 00:10.960 | SPEAKER_02: Text
    /^(\d{1,2}:\d{2}(?::\d{2})?(?:\.\d{1,3})?)\s*[→→|-]\s*(\d{1,2}:\d{2}(?::\d{2})?(?:\.\d{1,3})?)\s*\|\s*([^:]+):\s*(.+)$/,
  ];
  
  for (const line of lines) {
    let match: RegExpMatchArray | null = null;
    
    for (const pattern of patterns) {
      match = line.match(pattern);
      if (match) break;
    }
    
    if (!match) {
      console.warn(`Could not parse line: ${line}`);
      continue;
    }
    
    const [, startStr, endStr, speakerRaw, text] = match;
    const startTime = parseTimestamp(startStr);
    const endTime = parseTimestamp(endStr);
    const speakerName = speakerRaw.trim();
    
    // Normalize speaker ID
    const speakerId = speakerName.toUpperCase().replace(/[^A-Z0-9]/g, '_');
    
    // Create speaker if not exists
    if (!speakerMap.has(speakerId)) {
      speakerMap.set(speakerId, {
        id: speakerId,
        name: speakerName,
        color: SPEAKER_COLORS[colorIndex % SPEAKER_COLORS.length],
      });
      colorIndex++;
    }
    
    segments.push({
      id: `seg_${segments.length + 1}`,
      startTime,
      endTime,
      speakerId,
      text: text.trim(),
    });
  }
  
  const duration = segments.length > 0
    ? Math.max(...segments.map(s => s.endTime))
    : 0;
  
  return {
    id: `transcript_${Date.now()}`,
    title: options.title ?? 'Untitled Transcript',
    recordingDate: options.recordingDate ?? new Date(),
    duration,
    segments,
    speakers: Array.from(speakerMap.values()),
    metadata: {
      createdAt: new Date(),
      updatedAt: new Date(),
    },
  };
}

/**
 * Export transcript to different formats
 */
export function exportTranscript(
  transcript: Transcript,
  format: 'json' | 'txt' | 'srt' | 'vtt'
): string {
  switch (format) {
    case 'json':
      return JSON.stringify(transcript, null, 2);
      
    case 'txt':
      return transcript.segments
        .map((seg) => {
          const speaker = transcript.speakers.find(s => s.id === seg.speakerId);
          return `[${formatTimestamp(seg.startTime, true)} → ${formatTimestamp(seg.endTime, true)}] ${speaker?.name ?? 'Unknown'}: ${seg.text}`;
        })
        .join('\n');
      
    case 'srt':
      return transcript.segments
        .map((seg, i) => {
          const speaker = transcript.speakers.find(s => s.id === seg.speakerId);
          const start = formatSrtTimestamp(seg.startTime);
          const end = formatSrtTimestamp(seg.endTime);
          return `${i + 1}\n${start} --> ${end}\n${speaker?.name ?? 'Unknown'}: ${seg.text}\n`;
        })
        .join('\n');
      
    case 'vtt':
      const header = 'WEBVTT\n\n';
      const cues = transcript.segments
        .map((seg) => {
          const speaker = transcript.speakers.find(s => s.id === seg.speakerId);
          const start = formatVttTimestamp(seg.startTime);
          const end = formatVttTimestamp(seg.endTime);
          return `${start} --> ${end}\n<v ${speaker?.name ?? 'Unknown'}>${seg.text}\n`;
        })
        .join('\n');
      return header + cues;
      
    default:
      throw new Error(`Unsupported format: ${format}`);
  }
}

function formatSrtTimestamp(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const milliseconds = ms % 1000;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')},${milliseconds.toString().padStart(3, '0')}`;
}

function formatVttTimestamp(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const milliseconds = ms % 1000;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
}

/**
 * Calculate statistics from transcript
 */
export function calculateStats(transcript: Transcript) {
  return calculateStatsFromSegments(transcript.segments, transcript.speakers, transcript.duration);
}

/**
 * Calculate statistics from a subset of segments (for filtering)
 */
export function calculateStatsFromSegments(
  segments: TranscriptSegment[],
  speakers: Speaker[],
  totalDuration: number
) {
  const speakerStats = new Map<string, {
    totalTime: number;
    wordCount: number;
    segmentCount: number;
  }>();
  
  let totalWordCount = 0;
  
  for (const segment of segments) {
    const duration = segment.endTime - segment.startTime;
    const wordCount = segment.text.split(/\s+/).filter(w => w.length > 0).length;
    totalWordCount += wordCount;
    
    const existing = speakerStats.get(segment.speakerId) ?? {
      totalTime: 0,
      wordCount: 0,
      segmentCount: 0,
    };
    
    speakerStats.set(segment.speakerId, {
      totalTime: existing.totalTime + duration,
      wordCount: existing.wordCount + wordCount,
      segmentCount: existing.segmentCount + 1,
    });
  }
  
  // Calculate the actual total time from filtered segments for percentage calculation
  let filteredTotalTime = 0;
  for (const stats of speakerStats.values()) {
    filteredTotalTime += stats.totalTime;
  }
  
  return {
    totalDuration: filteredTotalTime > 0 ? filteredTotalTime : totalDuration,
    wordCount: totalWordCount,
    segmentCount: segments.length,
    averageSegmentDuration: segments.length > 0
      ? filteredTotalTime / segments.length
      : 0,
    speakerStats: speakers
      .filter(speaker => speakerStats.has(speaker.id))
      .map((speaker) => {
        const stats = speakerStats.get(speaker.id) ?? {
          totalTime: 0,
          wordCount: 0,
          segmentCount: 0,
        };
        return {
          speakerId: speaker.id,
          speakerName: speaker.name,
          color: speaker.color,
          totalTime: stats.totalTime,
          percentage: filteredTotalTime > 0 ? (stats.totalTime / filteredTotalTime) * 100 : 0,
          wordCount: stats.wordCount,
          segmentCount: stats.segmentCount,
          averageSegmentDuration: stats.segmentCount > 0
            ? stats.totalTime / stats.segmentCount
            : 0,
        };
      }),
  };
}
